import { GridConfig } from '../core/Grid.js';

export type GameType = 'mirrors' | 'knight' | 'walls' | 'splitting' | 'line' | 'antennas' | 'ignition' | 'flood' | 'orbits' | 'branches' | 'crossing' | 'deluge' | 'turns' | 'jumps';

/**
 * The full pool of games (14). A grid plays a **set** of at most 12 of these (see
 * {@link gridGameSet}); the classic 12 drop Turns and Jumps. Alphabetical for the default
 * sidebar order.
 */
export const GAME_TYPES: GameType[] = [
    'antennas', 'branches', 'crossing', 'deluge',
    'flood', 'ignition', 'jumps', 'knight', 'line',
    'mirrors', 'orbits', 'splitting', 'turns', 'walls'
];

/**
 * Rough difficulty order (easy → hard), used as an alternative to the alphabetical
 * game-button ordering. Curated by the maintainer (loosely tracks the solver's Game
 * Status Tracker in docs/solver.md). Edit this list to adjust the order shown when
 * the "difficulty" game-order preference is active.
 */
export const GAME_TYPES_BY_DIFFICULTY: GameType[] = [
    'mirrors', 'knight', 'branches', 'walls', 'crossing', 'jumps', 'splitting',
    'line', 'turns', 'antennas', 'orbits', 'flood', 'ignition', 'deluge'
];

export type GameOrder = 'alphabetical' | 'difficulty';

/** The 12 game types in the sequence implied by a game-order preference. */
export function orderedGameTypes(order: GameOrder): GameType[] {
    return order === 'difficulty' ? GAME_TYPES_BY_DIFFICULTY : GAME_TYPES;
}

export const GAME_INITIALS: Record<GameType, string> = {
    antennas: 'A',
    branches: 'B',
    crossing: 'C',
    deluge: 'D',
    flood: 'F',
    ignition: 'I',
    jumps: 'J',
    knight: 'K',
    line: 'L',
    mirrors: 'M',
    orbits: 'O',
    splitting: 'S',
    turns: 'T',
    walls: 'W'
};

export const INITIAL_TO_GAME: Record<string, GameType> = {
    'A': 'antennas',
    'B': 'branches',
    'C': 'crossing',
    'D': 'deluge',
    'F': 'flood',
    'I': 'ignition',
    'J': 'jumps',
    'K': 'knight',
    'L': 'line',
    'M': 'mirrors',
    'O': 'orbits',
    'S': 'splitting',
    'T': 'turns',
    'W': 'walls'
};

/**
 * The classic 12 — the default set a grid plays, **frozen as an explicit list**.
 *
 * Do not derive this from {@link GAME_TYPES}. It used to be `GAME_TYPES.filter(g => g !==
 * 'turns')`, which made the default set's *size* track the pool: adding a 14th game would
 * have silently turned every grid into a 13-game grid, grown `CLASSIC_SCOPE` to 13 chars, and
 * made `isValidScope` reject the 12-game scopes already stored in the challenge database —
 * all without a single type error. The pool may grow; this list is what a grid plays unless
 * its `gameSet` says otherwise, and it changes only by deliberate edit.
 *
 * Guarded by src/persistence/types.test.ts.
 */
export const CLASSIC_GAME_TYPES: GameType[] = [
    'antennas', 'branches', 'crossing', 'deluge',
    'flood', 'ignition', 'knight', 'line',
    'mirrors', 'orbits', 'splitting', 'walls',
];

/** Scope string for the classic 12, e.g. "ABCDFIKLMOSW". */
export const CLASSIC_SCOPE: string = CLASSIC_GAME_TYPES.map(g => GAME_INITIALS[g]).join('');

/** Parse a scope string ("ABCDFIKLMOSW") into game types, in canonical (pool) order. */
export function scopeToGameTypes(scope: string): GameType[] {
    const inScope = new Set(
        scope.split('').map(i => INITIAL_TO_GAME[i]).filter((g): g is GameType => !!g)
    );
    return GAME_TYPES.filter(g => inScope.has(g));
}

/** Build a sorted (canonical-order) scope string from a set of game types. */
export function gameTypesToScope(types: Iterable<GameType>): string {
    const set = new Set(types);
    return GAME_TYPES.filter(g => set.has(g)).map(g => GAME_INITIALS[g]).join('');
}

/**
 * The games a grid actually plays, in canonical order. A challenge-backed grid takes its
 * set from the challenge scope; otherwise the grid's own `gameSet`; otherwise the classic
 * 12 (Turns and Jumps dropped). So every legacy grid — with neither field — reads as the
 * classic 12.
 */
export function gridGameSet(grid: Pick<SavedGrid, 'challengeScope' | 'gameSet'>): GameType[] {
    const scope = grid.challengeScope || grid.gameSet;
    return scope ? scopeToGameTypes(scope) : CLASSIC_GAME_TYPES;
}

/**
 * Persisted game state holds only source-of-truth: the current serialized state
 * and the best (won) serialized state. Win status and scores are recomputed from
 * these on read — never stored.
 */
export interface GameState {
    serialized: string;
    bestSerialized?: string;
}

/** Backup of win states + Hub before a destructive gate removal. Scores recomputed. */
export interface VortexBackup {
    hubState: string;
    winStates: Partial<Record<GameType, string>>;  // gameType -> serialized win state
}

export interface SavedGrid {
    id: string;
    gridData: string;
    gridConfig?: GridConfig;  // Optional for backward compat with legacy grids
    lastPlayed: number;
    isImported?: boolean;
    // Authoring-only annotations (set on the authoring instance, reconciled against the
    // admin backlog; players never have these). A grid carries at most one: a candidate
    // becomes scheduled, and once its window opens the challenge fields below take over.
    /** This grid is a candidate in the admin backlog. */
    fromBacklog?: boolean;
    /** Scheduled as a challenge that has not started yet (so it isn't a ChallengeRef yet). */
    scheduledTitle?: string;
    scheduledStartsAt?: string;  // ISO 8601
    vortexGridData?: string;  // Serialized Vortex Hub (gate positions + link edges)
    vortexBackup?: VortexBackup;  // Backup of win states + Hub before gate removal
    /** The 12-game set this grid plays, as a sorted scope string (absent ⇒ classic 12).
     *  Chosen at create/duplicate; a challenge overrides it via challengeScope. */
    gameSet?: string;
    /**
     * Origin descriptor for a grid that was duplicated from a challenge or default grid,
     * e.g. a challenge title or "Default grid". Set on the copy (which is otherwise native
     * and lands in Custom Grids) so its card can still read "Copy · <origin>". Propagates
     * across further copies so provenance survives a copy-of-a-copy. Absent on originals
     * and on copies of plain custom/imported grids. */
    copyOf?: string;
    gameStates: Partial<Record<GameType, GameState>>;
    // Challenge linkage (set when this grid backs a server challenge):
    challengeId?: number;
    challengeTitle?: string;
    challengeScope?: string;   // sorted game initials in scope, e.g. "ABCDFIKLMOSW"
    challengeEndsAt?: string;  // ISO 8601
    challengeSubmitted?: string;  // solution_data of the last accepted submission (drives the amber "pending" state)
}

/**
 * The admin pipeline's view of which grids are candidates and which are scheduled,
 * used to reconcile the authoring annotations on SavedGrid. Authoring instance only.
 */
export interface AuthoringState {
    /** gridData of every grid currently in the backlog. */
    candidates: string[];
    /** Every scheduled challenge, started or not. */
    scheduled: Array<{ gridData: string; title: string; startsAt: string }>;
}

/** How challenge solutions are pushed to the leaderboard. */
export type SubmitMode = 'manual' | 'each' | 'full';

/** Minimal challenge identity used to link a grid to a server challenge. */
export interface ChallengeRef {
    id: number;
    title: string;
    gridData: string;
    scope: string;       // sorted game initials in scope
    endsAt: string;      // ISO 8601
    isVortex?: boolean;
}

/** User preferences, persisted alongside grids (additive; no schema bump needed). */
export interface UserPreferences {
    gameOrder?: GameOrder;
    submitMode?: SubmitMode;
    /** Mirrors: show the amber backward hint beam from the exit (default off). */
    mirrorsHintBeam?: boolean;
    /** Branches: show teal hint rings on edges that would extend the network (default on). */
    branchesHintRings?: boolean;
    /** Line: show chevron hints for start placement / next segment (default on). */
    lineHints?: boolean;
    /** Keyboard mode: crosshair cursor + keyboard placement (default off). */
    keyboardMode?: boolean;
}

export interface StorageData {
    version: number;
    currentGridId: string | null;
    grids: SavedGrid[];
    challengeClears?: Record<number, number>;  // Challenges cleared per grid size, e.g. {8: 1, 10: 0}
    clearedChallengeIds?: number[];  // challenge ids already counted in challengeClears (dedupe)
    preferences?: UserPreferences;
}

export const STORAGE_KEY = 'gridGames';
export const SCHEMA_VERSION = 5;
export const MAX_GRIDS = 100;
